import React, { useMemo, useState } from 'react';
import StbModal from '../security-toolbox-ui/StbModal';
import StbMarkdown from '../security-toolbox-ui/StbMarkdown';
import { useResourceFeedback } from '../../hooks/useResourceFeedback';

export type ResourceBucketKey =
  | 'What'
  | 'Why'
  | 'Sell'
  | 'Differentiate'
  | 'Configure'
  | 'Validate'
  | 'Troubleshoot';

export type ResourceDrillLink = {
  label: string;
  url: string;
};

export type ResourceDrillCapability = {
  bucket: ResourceBucketKey;
  title: string;
  summary?: string;
  markdown: string;
  links?: ResourceDrillLink[];
  sourceType?: 'product' | 'use_case' | 'component' | 'feature' | 'resource';
  sourceName?: string;
  sourceId?: string;
  createdAt?: string;
  updatedAt?: string;
  last_updated?: string;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
};

export type ResourceDrillEntity = {
  id: string;
  name: string;
  summary?: string;
  capabilities: Partial<Record<ResourceBucketKey, ResourceDrillCapability>>;
};

type ResourceDrillModalProps = {
  open: boolean;
  entity: ResourceDrillEntity | null;
  activeBucket: ResourceBucketKey;
  bucketOrder: ResourceBucketKey[];
  onBucketChange: (bucket: ResourceBucketKey) => void;
  onClose: () => Promise<void>;
  title?: string;
  userEmail: string;
  productId?: string | number;
  productName?: string;
};

const ALL_BUCKETS: ResourceBucketKey[] = [
  'What',
  'Why',
  'Sell',
  'Differentiate',
  'Configure',
  'Validate',
  'Troubleshoot',
];

function cleanEditedSuffix(value?: string): string {
  if (!value) return '';
  return value.replace(/\s*-\s*edited\b/gi, '').trim();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeComparableLine(value?: string): string {
  return cleanEditedSuffix(value || '')
    .replace(/^#+\s*/, '')
    .replace(/^[*-]\s*/, '')
    .replace(/^["'`]+|["'`]+$/g, '')
    .trim()
    .toLowerCase();
}

function isCiscoMulticloudDefenseHeaderVariant(
  line: string,
  bucket?: ResourceBucketKey
): boolean {
  if (!line || !bucket) return false;

  const normalizedLine = cleanEditedSuffix(line)
    .replace(/^#+\s*/, '')
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const normalizedBucket = bucket.trim().toLowerCase();

  const startsWithMulticloudDefense =
    normalizedLine.startsWith('cisco multicloud defense -') ||
    normalizedLine.startsWith('cisco multi-cloud defense -');

  const endsWithBucket =
    normalizedLine.endsWith(`- ${normalizedBucket}`) ||
    normalizedLine === normalizedBucket;

  return startsWithMulticloudDefense && endsWithBucket;
}

function stripLeadingHeaderNoise(
  markdown: string,
  options: {
    entityName?: string;
    capabilityTitle?: string;
    bucket?: ResourceBucketKey;
    sourceName?: string;
    modalTitle?: string;
  }
): string {
  if (!markdown) return '';

  const cleanEntityName = cleanEditedSuffix(options.entityName || '');
  const cleanCapabilityTitle = cleanEditedSuffix(options.capabilityTitle || '');
  const cleanSourceName = cleanEditedSuffix(options.sourceName || '');
  const cleanModalTitle = cleanEditedSuffix(options.modalTitle || '');
  const bucket = options.bucket;
  const lines = markdown.split('\n');

  const allBucketNames = ALL_BUCKETS.map((item) => item.toLowerCase());

  const directMatches = [
    cleanModalTitle,
    cleanEntityName,
    cleanCapabilityTitle,
    cleanSourceName,
    bucket,
    `${cleanEntityName} — ${bucket}`,
    `${cleanEntityName} - ${bucket}`,
    `${cleanSourceName} — ${bucket}`,
    `${cleanSourceName} - ${bucket}`,
    `${cleanModalTitle} - ${cleanSourceName} - ${bucket}`,
    `${cleanModalTitle} — ${cleanSourceName} — ${bucket}`,
    `${cleanModalTitle} - ${cleanSourceName}`,
    `${cleanEntityName} - ${cleanSourceName}`,
    `${cleanEntityName} - ${cleanSourceName} - ${bucket}`,
    `${cleanModalTitle} - ${bucket}`,
  ]
    .map((item) => normalizeComparableLine(item))
    .filter(Boolean);

  const modalPattern = cleanModalTitle ? escapeRegExp(cleanModalTitle) : '';
  const entityPattern = cleanEntityName ? escapeRegExp(cleanEntityName) : '';
  const sourcePattern = cleanSourceName ? escapeRegExp(cleanSourceName) : '';
  const bucketPattern = bucket ? escapeRegExp(bucket) : '';

  const exactPatterns = [
    modalPattern && sourcePattern && bucketPattern
      ? new RegExp(`^${modalPattern}\\s*[-—]\\s*${sourcePattern}\\s*[-—]\\s*${bucketPattern}$`, 'i')
      : null,
    entityPattern && sourcePattern && bucketPattern
      ? new RegExp(`^${entityPattern}\\s*[-—]\\s*${sourcePattern}\\s*[-—]\\s*${bucketPattern}$`, 'i')
      : null,
    sourcePattern && bucketPattern
      ? new RegExp(`^${sourcePattern}\\s*[-—]\\s*${bucketPattern}$`, 'i')
      : null,
    modalPattern && sourcePattern
      ? new RegExp(`^${modalPattern}\\s*[-—]\\s*${sourcePattern}$`, 'i')
      : null,
    entityPattern && sourcePattern
      ? new RegExp(`^${entityPattern}\\s*[-—]\\s*${sourcePattern}$`, 'i')
      : null,
  ].filter(Boolean) as RegExp[];

  let index = 0;

  while (index < lines.length) {
    const rawLine = lines[index] || '';
    const trimmed = rawLine.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    const normalized = normalizeComparableLine(trimmed);

    if (!normalized) {
      index += 1;
      continue;
    }

    if (allBucketNames.includes(normalized)) {
      index += 1;
      continue;
    }

    if (directMatches.includes(normalized)) {
      index += 1;
      continue;
    }

    if (exactPatterns.some((pattern) => pattern.test(cleanEditedSuffix(trimmed)))) {
      index += 1;
      continue;
    }

    if (isCiscoMulticloudDefenseHeaderVariant(trimmed, bucket)) {
      index += 1;
      continue;
    }

    break;
  }

  let cleaned = lines.slice(index).join('\n').trim();

  if (!cleaned) return cleaned;

  const cleanedSourceName = cleanEditedSuffix(cleanSourceName);

  if (cleanedSourceName) {
    const firstParagraphPrefixPattern = new RegExp(
      `^${escapeRegExp(cleanedSourceName)}\\s*-\\s*edited\\b[\\s,:-]*`,
      'i'
    );

    cleaned = cleaned.replace(firstParagraphPrefixPattern, '');

    const firstParagraphDuplicateNamePattern = new RegExp(
      `^${escapeRegExp(cleanedSourceName)}\\b[\\s,:-]*`,
      'i'
    );

    const withoutEdited = cleanEditedSuffix(cleaned);
    if (withoutEdited !== cleaned) {
      cleaned = withoutEdited;
    }

    if (firstParagraphDuplicateNamePattern.test(cleaned)) {
      cleaned = cleaned.replace(firstParagraphDuplicateNamePattern, '').trimStart();
    }
  }

  cleaned = cleaned.trim();

  if (cleaned) {
    const firstAlphaIndex = cleaned.search(/[a-zA-Z]/);
    if (firstAlphaIndex >= 0) {
      cleaned =
        cleaned.slice(0, firstAlphaIndex) +
        cleaned.charAt(firstAlphaIndex).toUpperCase() +
        cleaned.slice(firstAlphaIndex + 1);
    }
  }

  return cleaned;
}

function formatTimestamp(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const ResourceDrillModal: React.FC<ResourceDrillModalProps> = ({
                                                                 open,
                                                                 entity,
                                                                 activeBucket,
                                                                 bucketOrder,
                                                                 onBucketChange,
                                                                 onClose,
                                                                 title,
                                                                 userEmail,
                                                                 productId,
                                                                 productName,
                                                               }) => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');

  const capabilities = entity?.capabilities || {};
  const selectedCapability = capabilities[activeBucket];
  const resourceId = selectedCapability?.sourceId || '';

  const { isFlagged, isSubmitting, submitFeedback } = useResourceFeedback({
    resourceId,
    enabled: open && !!resourceId,
  });

  const cleanedModalTitle = cleanEditedSuffix(title || 'Detail');
  const cleanedEntityName = cleanEditedSuffix(entity?.name || '');
  const cleanedItemTitle = cleanEditedSuffix(
    selectedCapability?.sourceName
      ? `${selectedCapability.sourceName} - ${activeBucket}`
      : selectedCapability?.title || ''
  );

  const cleanedMarkdown = useMemo(() => {
    if (!selectedCapability?.markdown) return '';
    return stripLeadingHeaderNoise(selectedCapability.markdown, {
      entityName: entity?.name,
      capabilityTitle: selectedCapability.title,
      bucket: activeBucket,
      sourceName: selectedCapability.sourceName,
      modalTitle: cleanedModalTitle,
    });
  }, [selectedCapability, entity?.name, activeBucket, cleanedModalTitle]);

  const lastUpdatedValue =
    selectedCapability?.last_updated ||
    selectedCapability?.updatedAt ||
    selectedCapability?.updated_at ||
    selectedCapability?.createdAt ||
    selectedCapability?.created_at;

  const timestampLabel = lastUpdatedValue
    ? `${selectedCapability?.last_updated || selectedCapability?.updatedAt || selectedCapability?.updated_at ? 'Last updated' : 'Created'} ${formatTimestamp(lastUpdatedValue)}`
    : '';

  const handleCloseFeedback = async (): Promise<void> => {
    setFeedbackOpen(false);
    setFeedbackText('');
  };

  const handleSubmitFeedback = async (): Promise<void> => {
    if (!entity || !selectedCapability || !feedbackText.trim() || !resourceId) return;

    await submitFeedback({
      resourceId,
      userEmail,
      details: feedbackText.trim(),
      entityId: entity.id,
      entityName: cleanedEntityName || entity.name,
      bucket: activeBucket,
      sourceType: selectedCapability.sourceType,
      productId,
      productName,
    });

    setFeedbackOpen(false);
    setFeedbackText('');
  };

  return (
    <>
      <StbModal
        open={open}
        title={cleanedModalTitle}
        size="x-large"
        scrollable
        hideCancel
        submitLabel="Close"
        submitButtonRole="default"
        onClose={onClose}
        onSubmit={onClose}
      >
        <div className="resource-drill-modal">
          {entity?.summary ? (
            <div className="resource-drill-modal__summary">{entity.summary}</div>
          ) : null}

          <div className="resource-drill-modal__bucket-tabs">
            {bucketOrder.map((bucket) => {
              const hasContent = !!capabilities[bucket];
              return (
                <button
                  key={bucket}
                  type="button"
                  disabled={!hasContent}
                  className={`resource-drill-modal__bucket-tab ${
                    activeBucket === bucket ? 'active' : ''
                  } ${!hasContent ? 'disabled' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!hasContent) return;
                    onBucketChange(bucket);
                  }}
                >
                  {bucket}
                </button>
              );
            })}
          </div>

          {cleanedItemTitle ? (
            <div className="resource-drill-modal__item-title">{cleanedItemTitle}</div>
          ) : null}

          <div className="resource-drill-modal__content">
            {!entity ? (
              <div className="resource-drill-modal__empty">No item selected.</div>
            ) : !selectedCapability ? (
              <div className="resource-drill-modal__empty">No content available for this bucket.</div>
            ) : (
              <div className="resource-capability-panel">
                {selectedCapability.summary ? (
                  <div className="resource-capability-panel__summary">
                    {selectedCapability.summary}
                  </div>
                ) : null}

                {cleanedMarkdown ? (
                  <div className="resource-capability-panel__markdown">
                    <StbMarkdown>{cleanedMarkdown}</StbMarkdown>
                  </div>
                ) : null}

                {!!selectedCapability.links?.length && (
                  <div className="resource-capability-panel__links">
                    {selectedCapability.links.map((link, index) => (
                      <a
                        key={`${link.url}-${index}`}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="resource-capability-panel__link"
                      >
                        {link.label || link.url}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedCapability ? (
            <div className="resource-drill-modal__footer-meta">
              <div className="resource-drill-modal__footer-left">
                <div className="resource-drill-modal__footer-meta-left">
                  {timestampLabel ? (
                    <div className="resource-drill-modal__timestamp">
                      {timestampLabel}
                    </div>
                  ) : null}
                </div>

                <div className="resource-drill-modal__footer-meta-right">
                  {isFlagged ? (
                    <div className="resource-drill-modal__flagged-indicator">
                      Flagged
                    </div>
                  ) : null}

                  <button
                    type="button"
                    className="resource-drill-modal__feedback-button"
                    onClick={() => setFeedbackOpen(true)}
                  >
                    {isFlagged ? 'Flag again' : 'Flag content'}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </StbModal>

      <StbModal
        open={feedbackOpen}
        title="Flag content"
        size="medium"
        hideCancel={false}
        cancelLabel="Cancel"
        submitLabel={isSubmitting ? 'Submitting...' : 'Submit'}
        submitButtonRole="default"
        onClose={handleCloseFeedback}
        onSubmit={handleSubmitFeedback}
      >
        <div className="resource-feedback-modal">
          <div className="resource-feedback-modal__context">
            {cleanedEntityName ? (
              <div>
                <strong>Item:</strong> {cleanedEntityName}
              </div>
            ) : null}
            <div>
              <strong>Bucket:</strong> {activeBucket}
            </div>
          </div>

          <label className="resource-feedback-modal__label" htmlFor="resource-feedback-details">
            Describe what is incorrect about this content
          </label>
          <textarea
            id="resource-feedback-details"
            className="resource-feedback-modal__textarea"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            rows={6}
            placeholder="Add details about the issue..."
          />
        </div>
      </StbModal>
    </>
  );
};

export default ResourceDrillModal;